import { useState, type ReactNode } from "react";
import {
  useAbcAnalytics,
  isOrderNow,
  isTrappedCapital,
  abcClassSummaries,
  type AbcProduct,
  type AbcClass,
} from "@/hooks/use-analytics";
import { AbcPareto } from "@/components/analytics/abc-pareto";
import { AbcTable } from "@/components/analytics/abc-table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrency, formatNumber } from "@/lib/utils";

const fmtKM = (v: number) => formatCurrency(v, "KM");

export function AbcTab({ storeId, after, before }: { storeId: string; after: string; before: string }) {
  const [metric, setMetric] = useState<"revenue" | "quantity">("revenue");
  const [hideNewCDays, setHideNewCDays] = useState(0);
  const { data, isLoading, error, refetch, isFetching } = useAbcAnalytics(storeId, after, before, metric, true);

  if (isLoading) return <div className="flex flex-col gap-4"><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;

  if (error) {
    const code = (error as { code?: string }).code;
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <p className="text-sm font-medium">
          {code === "not_webhook" ? "ABC analiza zahtijeva webhook mod" :
           code === "backfill_running" ? "Sinhronizacija u toku" : "Greška pri učitavanju"}
        </p>
        <p className="text-xs text-muted-foreground max-w-md">{(error as Error).message}</p>
        {code === "not_webhook" && (
          <p className="text-xs text-muted-foreground">Webhook mod se uključuje u postavkama trgovine (Trgovine → Sinhronizacija).</p>
        )}
      </div>
    );
  }
  if (!data) return null;

  // „Sakrij C mlađe od N dana" — klijentski filter na dateCreated.
  // Bez useMemo: poziv bi bio poslije uslovnih return-ova (rules-of-hooks), a filter je jeftin.
  const cutoff = Date.now() - hideNewCDays * 86400000;
  const visible = data.products.filter((p) =>
    !(hideNewCDays > 0 && p.abcClass === "C" && p.dateCreated && new Date(p.dateCreated).getTime() > cutoff));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-0 border border-foreground/15 rounded-md overflow-hidden">
          {(["revenue", "quantity"] as const).map((m) => (
            <button key={m} type="button" onClick={() => setMetric(m)}
              className={cn("px-3 py-1 text-xs font-medium",
                metric === m ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}>
              {m === "revenue" ? "Prihod" : "Količina"}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Sakrij C mlađe od
          <input type="number" min={0} value={hideNewCDays}
            onChange={(e) => setHideNewCDays(Math.max(0, Number(e.target.value) || 0))}
            className="w-14 h-7 px-1.5 text-xs border border-foreground/15 rounded-md bg-transparent text-foreground" />
          dana
        </label>
        <button type="button" onClick={() => refetch()} disabled={isFetching}
          className="ml-auto px-3 py-1 text-xs font-medium border border-foreground/15 rounded-md hover:bg-foreground/5 disabled:opacity-50">
          {isFetching ? "Osvježavam…" : "Osvježi"}
        </button>
      </div>

      <SummaryCards visible={visible} />
      <div className="grid gap-4 lg:grid-cols-2">
        <ClassStructure visible={visible} />
        <AbcPareto pareto={data.pareto} metric={metric} />
      </div>
      <AbcTable products={visible} metric={metric} />
    </div>
  );
}

function SummaryCards({ visible }: { visible: AbcProduct[] }) {
  // Uslovi filtriranja dolaze iz `isOrderNow`/`isTrappedCapital` (isti predikati koje
  // koristi server za `summary`), pa kartice i server ostaju usklađeni po konstrukciji.
  // Oba predikata traže `stock !== null`, pa u karticama nema proizvoda s nepoznatom zalihom.
  const orderNow = visible.filter(isOrderNow).sort((a, b) => a.coverageDays! - b.coverageDays!);
  const trapped = visible.filter(isTrappedCapital).sort((a, b) => b.stockValue - a.stockValue);
  const trappedTotal = trapped.reduce((s, p) => s + p.stockValue, 0);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <SummaryCard tone="red" title="Naruči odmah" subtitle="A klasa sa zalihom ispod 14 dana"
        headline={`${orderNow.length}`} headlineSub="proizvoda" items={orderNow.slice(0, 7)}
        renderRight={(p) => <span className="text-red-600 font-semibold text-sm tabular-nums">{formatNumber(Math.round(p.coverageDays!))} d</span>}
        renderSub={(p) => `${formatNumber(p.stock!)} kom na stanju · prodaja ${formatNumber(p.dailySales, 2)}/dan`}
        moreCount={Math.max(0, orderNow.length - 7)} />
      <SummaryCard tone="amber" title="Zarobljeni kapital"
        subtitle="C klasa sa zalihom preko 90 dana ili bez prodaje — kandidati za sniženje"
        headline={fmtKM(trappedTotal)} headlineSub={`${trapped.length} proizvoda`} items={trapped.slice(0, 7)}
        renderRight={(p) => <span className="text-amber-600 font-semibold text-sm tabular-nums">{fmtKM(p.stockValue)}</span>}
        renderSub={(p) => p.noSales
          ? `${formatNumber(p.stock!)} kom · bez prodaje u periodu`
          : `${formatNumber(p.stock!)} kom · zaliha za ${formatNumber(Math.round(p.coverageDays!))} d`}
        moreCount={Math.max(0, trapped.length - 7)} />
    </div>
  );
}

function SummaryCard({
  tone, title, subtitle, headline, headlineSub, items, renderRight, renderSub, moreCount,
}: {
  tone: "red" | "amber";
  title: string;
  subtitle: string;
  headline: string;
  headlineSub: string;
  items: AbcProduct[];
  renderRight: (p: AbcProduct) => ReactNode;
  renderSub: (p: AbcProduct) => string;
  moreCount: number;
}) {
  return (
    <div className="ring-1 ring-foreground/10 flex flex-col">
      <div className={cn("px-3 py-2.5 flex items-baseline gap-2",
        tone === "red" ? "bg-red-50 dark:bg-red-950/30" : "bg-amber-50 dark:bg-amber-950/30")}>
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <span className="text-xs font-medium">{title}</span>
          <span className="text-xs text-muted-foreground">{subtitle}</span>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <span className="text-sm font-semibold tabular-nums">{headline}</span>
          <span className="text-xs text-muted-foreground">{headlineSub}</span>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="px-3 py-4 text-xs text-muted-foreground">Nema proizvoda u ovoj kategoriji.</p>
      ) : (
        <div className="divide-y">
          {items.map((p) => (
            <div key={p.productId} className="flex items-center gap-3 px-3 py-2">
              {p.imageUrl ? (
                <img src={p.imageUrl} alt="" className="w-10 h-10 shrink-0 object-cover ring-1 ring-foreground/10" />
              ) : (
                <div className="w-10 h-10 shrink-0 bg-foreground/10 ring-1 ring-foreground/10" />
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-medium text-sm truncate">{p.name}</span>
                <span className="text-xs text-muted-foreground truncate">{renderSub(p)}</span>
              </div>
              <div className="shrink-0">{renderRight(p)}</div>
            </div>
          ))}
        </div>
      )}

      {moreCount > 0 && (
        <div className="px-3 py-2 border-t text-xs text-muted-foreground">
          + još {moreCount} proizvoda — svi su u tabeli ispod.
        </div>
      )}
    </div>
  );
}

const CLASS_META: Record<AbcClass, { title: string; hint: string; bar: string }> = {
  A: { title: "Nosioci prihoda", hint: "do 80 % prihoda", bar: "bg-emerald-500" },
  B: { title: "Sredina", hint: "80–95 %", bar: "bg-amber-500" },
  C: { title: "Rep", hint: "zadnjih 5 % i bez prodaje", bar: "bg-slate-400" },
};

// Sve četiri brojke se računaju iz `visible` (vidi `abcClassSummaries`), ne iz
// `data.classes` — serverove sume pripadaju punom skupu proizvoda.
function ClassStructure({ visible }: { visible: AbcProduct[] }) {
  const classes = abcClassSummaries(visible);

  return (
    <div className="ring-1 ring-foreground/10">
      <div className="px-3 py-2.5 border-b">
        <span className="text-xs font-medium">Struktura asortimana</span>
      </div>
      <div className="divide-y">
        {classes.map((c) => {
          const meta = CLASS_META[c.abcClass];
          return (
            <div key={c.abcClass} className="flex flex-col gap-2 px-3 py-3">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-medium">{c.abcClass} — {meta.title}</span>
                <span className="text-xs text-muted-foreground">{meta.hint}</span>
                <span className="ml-auto text-xs tabular-nums text-muted-foreground shrink-0">{c.productCount} proizvoda</span>
              </div>
              <ClassBar label="Prihod" share={c.revenueShare} value={c.revenue} bar={meta.bar} />
              <ClassBar label="Zaliha" share={c.stockShare} value={c.stockValue} bar={meta.bar} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClassBar({ label, share, value, bar }: { label: string; share: number; value: number; bar: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground w-12 shrink-0">{label}</span>
      <div className="h-1.5 flex-1 min-w-0 bg-foreground/10">
        <div className={cn("h-full", bar)} style={{ width: `${Math.min(100, Math.max(0, share))}%` }} />
      </div>
      <span className="tabular-nums text-muted-foreground shrink-0">{share}% · {fmtKM(value)}</span>
    </div>
  );
}
